import {
  add,
  compareAsc,
  differenceInDays,
  differenceInYears,
  eachDayOfInterval,
  getDay,
  sub
} from 'date-fns'
import { isNumber, maxBy } from 'lodash'
import md5 from 'md5'
import { customAlphabet } from 'nanoid'

import {
  fullDateFormat,
  minLettersAlphabet,
  simpleDateFormat
} from '@/constants'
import { PatientsModel } from '@/redux/patients/types'
import { SchedulesModel } from '@/redux/schedules/types'
import {
  addBatchScheduleNote,
  deleteBatchScheduleNote,
  editBatchScheduleNote
} from '@/services/scheduleNotes'
import {
  addBatchStNote,
  deleteBatchStNote,
  editBatchStNote
} from '@/services/stNotes'
import { notesItem, ScheduledExercise, StScheduledExercise } from '@/types'
import { localizedFormat } from './format'

export function isCurrentLocation (location: string, menuItemUrl: string) {
  return location === menuItemUrl
}

export function createPassword (length = 8) {
  const password = generatePassword(length)
  const hashed = md5(password)

  return { natural: password, hashed }
}

const generatePassword = (length: number) => {
  const randomChars = customAlphabet(minLettersAlphabet, length)

  const randomCharsText = randomChars()

  return randomCharsText
}

export function calcAge (isoDate: string) {
  const date = new Date(isoDate)
  const age = differenceInYears(new Date(), date)

  return age
}

export function allSchedulesDates (
  startDate: string,
  totalCalendarDays: number
) {
  const start = new Date(startDate)

  const result = eachDayOfInterval({
    start,
    end: add(start, { days: totalCalendarDays - 1 })
  })

  return result.map(item => localizedFormat(item, fullDateFormat))
}

export function getWeekDayNumber (day: number) {
  const weeklyDay = getDay(day)

  return weeklyDay === 0 ? 7 : weeklyDay
}

/**
 * Organizes a list of dates into a paginated calendar structure.
 *
 * Takes an array of date strings and returns a two-dimensional array where:
 * - Each inner array represents a complete week (7 days)
 * - The first week is padded with days before the first provided date to start from the beginning of the week
 * - The last week is padded with days after the last provided date to end at the end of the week
 *
 * This creates a clean calendar-like structure suitable for UI rendering where complete weeks are displayed.
 *
 * @param {string[]} scheduleDates - Array of date strings to be organized into weeks
 * @returns {string[][]} Two-dimensional array of dates organized by weeks
 */
export function paginatedScheduleDates (scheduleDates: string[]) {
  const keyDates = [...scheduleDates]

  if (keyDates.length === 0) {
    return []
  }

  const firstDay = new Date(keyDates[0]).setHours(0, 0, 0, 0)
  const lastDay = new Date(keyDates[keyDates.length - 1]).setHours(0, 0, 0, 0)

  const firstWeekDayNumber = getWeekDayNumber(firstDay)
  const lastWeekDayNumber = getWeekDayNumber(lastDay)

  for (let i = 1; i < firstWeekDayNumber; i += 1) {
    const calcDate = sub(firstDay, { days: i })
    keyDates.unshift(localizedFormat(calcDate, fullDateFormat))
  }

  for (let i = 1; i < 8 - lastWeekDayNumber; i += 1) {
    const calcDate = add(lastDay, { days: i })
    keyDates.push(localizedFormat(calcDate, fullDateFormat))
  }

  const dates = []

  for (let i = 0; i < keyDates?.length; i += 7) {
    const chunk = keyDates.slice(i, i + 7)
    dates.push(chunk)
  }

  return dates
}

export function getDayNumber (
  date: string,
  scheduleData: SchedulesModel | undefined
) {
  if (!scheduleData) return

  const diffInDays = differenceInDays(
    new Date(date).setHours(0, 0, 0, 0),
    new Date(scheduleData?.startDate).setHours(0, 0, 0, 0)
  )

  if (diffInDays < 0 || diffInDays >= scheduleData?.totalCalendarDays) {
    return null
  }

  const molt = Math.floor(diffInDays / scheduleData?.totalDays)
  const day = diffInDays - molt * scheduleData?.totalDays + 1

  return day
}

export function dateIsEqualOrLessToday (finalDate: Date) {
  const todayDate = new Date().setHours(0, 0, 0, 0)
  const dateComparison = compareAsc(todayDate, finalDate)

  if (dateComparison === -1 || dateComparison === 0) return true

  return false
}

/**
 * Calculates chart data for remaining days of schedules.
 *
 * This function:
 * 1. Filters schedules to only include those whose end date is today or earlier
 * 2. Finds the longest schedule among the filtered ones
 * 3. Calculates the number of days that have been completed in this schedule
 * 4. Returns an object containing the completed days and total calendar days
 *
 * @param schedules - Array of schedule objects to analyze
 * @returns An object containing `completedDays` and `totalCalendarDays`, or undefined if no eligible schedules exist
 */
export function getRemainingWeeksChartData (schedules: SchedulesModel[]) {
  const filteredSchedules = schedules.filter(schedule => {
    const finalDate = add(new Date(schedule.startDate), {
      days: schedule.totalCalendarDays - 1
    })

    return dateIsEqualOrLessToday(finalDate)
  })

  if (!filteredSchedules || filteredSchedules.length === 0) return

  const longestSchedule = maxBy(filteredSchedules, 'totalCalendarDays')

  if (!longestSchedule) return

  const { startDate, totalCalendarDays } = longestSchedule

  const daysFromStart = getCompletedScheduleDays(startDate)

  return {
    completedDays:
      daysFromStart > totalCalendarDays ? totalCalendarDays : daysFromStart,
    totalCalendarDays
  }
}

/**
 * Calculates the number of days that have passed from the provided start date until today.
 *
 * @param startDate - The starting date as a string
 * @returns The number of days between the start date and today.
 *          Returns 0 if the start date is in the future.
 *
 */
export function getCompletedScheduleDays (startDate: string) {
  const startTs = new Date(startDate).setHours(0, 0, 0, 0)
  const endTs = new Date().setHours(0, 0, 0, 0)

  // Handle future schedule start case
  if (startTs > endTs) return 0

  const remainingDays = differenceInDays(endTs, startTs)

  return remainingDays
}

export function calcNextDayDate (date: string, totalDays: number) {
  return localizedFormat(
    add(new Date(date), { days: totalDays }),
    fullDateFormat
  )
}

/**
 * Determines if a date is either in the past or today.
 *
 * @param date - A string representation of a date
 * @returns `true` if the given date is today or in the past, `false` otherwise
 */
export function isPastDateOrToday (date: string) {
  return new Date(date).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0)
}

export const getPatientOptions = (patientsList: PatientsModel[]) => {
  return patientsList.map(({ id, firstName, lastName, dateOfBirth }) => {
    const dateBirthFormatted = dateOfBirth
      ? localizedFormat(new Date(dateOfBirth), simpleDateFormat)
      : '-'

    return {
      value: id,
      label: `${firstName} ${lastName} | ${dateBirthFormatted}`
    }
  })
}

/**
 * Filters custom schedule templates based on body part or label matching the search text.
 *
 * @param option - The schedule template option object containing data with bodyParts and label
 * @param searchText - The text to search for within bodyParts and label
 * @returns Boolean indicating whether the option matches the search criteria
 */
export function customScheduleTemplatesFilterByBodyPart (
  option: any,
  searchText: string
) {
  if (
    option.data.bodyParts.toLowerCase().includes(searchText.toLowerCase()) ||
    option.data.label.toLowerCase().includes(searchText.toLowerCase())
  ) {
    return true
  }

  return false
}

/**
 * Processes changes to notes by comparing new and old values and executing appropriate API calls.
 *
 * This function handles three main operations:
 * 1. Adding new notes (identified by id === -1)
 * 2. Editing existing notes (when content has changed)
 * 3. Deleting notes (when they exist in old array but not in new array)
 *
 * @param newNotesValue - Array of note items with updated content
 * @param oldNotesValue - Array of existing notes with their current content
 * @param scheduleId - ID of the schedule being modified
 * @param tplNotes - Flag to determine whether to use template-specific functions (default: false)
 *
 * @returns Promise<boolean> - Resolves to true if all operations succeed, false otherwise
 */
export async function handleEditNotesForm (
  newNotesValue: notesItem[],
  oldNotesValue: {
    noteContent: string
    id: number
    order: number
  }[],
  scheduleId: number,
  tplNotes: boolean = false
) {
  let addNotesFn = addBatchScheduleNote
  let deleteNotesFn = deleteBatchScheduleNote
  let editNotesFn = editBatchScheduleNote

  if (tplNotes) {
    addNotesFn = addBatchStNote
    deleteNotesFn = deleteBatchStNote
    editNotesFn = editBatchStNote
  }

  const maxValueOrder = maxBy(oldNotesValue, 'order')?.order || 0

  const newNotes = newNotesValue
    .filter(({ id }) => id === -1)
    .map(({ text }) => text)

  // Get edited notes list
  const editedNotes = newNotesValue.filter(({ id, text }) => {
    if (id === -1) return false

    const savedValue = oldNotesValue.find(
      ({ id: savedNoteId }) => savedNoteId === id
    )

    return savedValue?.noteContent !== text
  })

  const notesArrayList = newNotesValue.map(({ id }) => id)
  const deletedNotes: number[] = oldNotesValue
    .filter(({ id }) => {
      return !notesArrayList.includes(id)
    })
    .map(({ id }) => id)

  try {
    if (newNotes?.length > 0) {
      await addNotesFn({
        scheduleId,
        notes: newNotes.map((note, index) => ({
          text: note,
          order: maxValueOrder + index + 1
        }))
      })
    }

    if (deletedNotes?.length > 0) {
      await deleteNotesFn({
        scheduleId,
        noteIds: deletedNotes
      })
    }

    if (editedNotes?.length > 0) {
      await editNotesFn({
        scheduleId,
        notes: editedNotes.map(({ id, text }) => ({
          id,
          text
        }))
      })
    }

    return true
  } catch (e) {
    console.log('Error while saving notes', e)

    return false
  }
}

/**
 * Calculates the next available sequence order value for exercises.
 *
 * This function examines an array of scheduled exercises, finds the highest
 * existing sequence order value, and returns that value incremented by 1.
 * If no exercises exist or no valid sequence order is found, returns 0.
 *
 * @param exercises - Array of scheduled exercises to analyze
 * @returns The next available sequence order value (max + 1 or 0 if none found)
 */
export function getMaxSequenceOrderVal (exercises: ScheduledExercise[] | StScheduledExercise[]) {
  const maxValueOrder = maxBy(
    exercises as { sequenceOrder: number }[],
    'sequenceOrder'
  )?.sequenceOrder

  let orderVal = 0
  if (isNumber(maxValueOrder)) {
    orderVal = maxValueOrder + 1
  }

  return orderVal
}
